import { FC } from 'react';

import { findRootSvgElement } from '@adflow/ad';
import type { AdElement, Animation } from '@adflow/types';
import type { ElementAnimationStyle } from '@adflow/utils';
import { Box, Stack, Text } from '@chakra-ui/react';

import { useElementSize } from '../../hooks/useElementSize';
import { EditorCanvasControls } from './EditorCanvasControls';
import { EditorCanvasStage } from './EditorCanvasStage';
import { useEditorCanvasSession } from './useEditorCanvasSession';

/** Deterministic size for unit tests and lab fixtures. */
export const EDITOR_CANVAS_TEST_STAGE_SIZE = { width: 520, height: 400 } as const;

export type EditorCanvasProps = {
  elements: AdElement[];
  resolvedElements: AdElement[];
  animationOverrides?: ReadonlyMap<string, ElementAnimationStyle> | null;
  animation?: Animation | null;
  selectedElementId: string | null;
  onSelectElement: (elementId: string | null) => void;
  onElementsChange: (elements: AdElement[]) => void;
  stageSize?: { width: number; height: number };
  fillContainer?: boolean;
  clipToFrame?: boolean;
  showControls?: boolean;
  stageTestId?: string;
};

export const EditorCanvas: FC<EditorCanvasProps> = ({
  elements,
  resolvedElements,
  animationOverrides = null,
  animation = null,
  selectedElementId,
  onSelectElement,
  onElementsChange,
  stageSize: stageSizeProp,
  fillContainer = false,
  clipToFrame = false,
  showControls = true,
  stageTestId,
}) => {
  const { ref: stageMeasureRef, size: measuredStageSize } = useElementSize<HTMLDivElement>();
  const stageSize =
    stageSizeProp ??
    (fillContainer && measuredStageSize.width > 0 && measuredStageSize.height > 0
      ? measuredStageSize
      : EDITOR_CANVAS_TEST_STAGE_SIZE);

  const root = findRootSvgElement(resolvedElements);
  const frame = {
    width: root?.size.width ?? 300,
    height: root?.size.height ?? 250,
  };

  const session = useEditorCanvasSession({
    elements,
    resolvedElements,
    animationOverrides,
    animation,
    frame,
    stageSize,
    selectedElementId,
    onSelectElement,
    onElementsChange,
    clipToFrame,
  });

  const canvasBody = (
    <>
      {showControls ? <EditorCanvasControls session={session} /> : null}
      <Text fontSize="xs" color="gray.500" flexShrink={0}>
        Alt+drag to pan. Drag elements inside the frame bounds.
      </Text>
      {fillContainer ? (
        <Box ref={stageMeasureRef} flex={1} minH={0} width="100%" display="flex">
          <EditorCanvasStage
            session={session}
            frame={frame}
            stageSize={stageSize}
            animationOverrides={animationOverrides}
            testId={stageTestId}
            flex={1}
          />
        </Box>
      ) : (
        <EditorCanvasStage
          session={session}
          frame={frame}
          stageSize={stageSize}
          animationOverrides={animationOverrides}
          testId={stageTestId}
        />
      )}
    </>
  );

  if (fillContainer) {
    return (
      <Stack
        spacing={2}
        align="stretch"
        flex={1}
        minH={0}
        width="100%"
        height="100%"
        data-test-id="editor-canvas"
      >
        {canvasBody}
      </Stack>
    );
  }

  return (
    <Stack spacing={2} align="stretch" data-test-id="editor-canvas">
      {canvasBody}
    </Stack>
  );
};

EditorCanvas.displayName = 'EditorCanvas';

export { useEditorCanvasSession } from './useEditorCanvasSession';
export type { EditorCanvasSession, UseEditorCanvasSessionArgs } from './useEditorCanvasSession';
export { EditorCanvasStage } from './EditorCanvasStage';
export { EditorCanvasControls } from './EditorCanvasControls';
